import React from 'react';
import { graphql } from 'gatsby';
import { RichTextBlock } from 'prismic-reactjs';
import SEO from '@components/seo';
import Layout from '@layouts/default-layout';
import Wrapper from '@components/wrapper.styled';
import Text from '@components/text';
import ContactForm from '@components/contact-form';
import {
  Container,
  ContactSection,
  TextContainer,
  InfoText,
} from '@components/contact-form/contact-form.styled';

interface ContactPageProps {
  edges: {
    node: {
      data: {
        page_title: {
          text: string;
        };
        meta_description?: {
          text: string;
        };
        meta_image?: {
          url: string;
          alt: string;
        };
        portal_id: string;
        form_id: string;
        form_title: {
          text: string;
        };
        form_subheading: {
          richText: RichTextBlock[];
        };
        additional_info: {
          richText: RichTextBlock[];
        };
      };
    };
  }[];
}

interface ContactInterface {
  data: {
    prismicGeneralConfig: {
      data: {
        favicon?: {
          url?: string;
        };
      };
    };
    allPrismicContact: ContactPageProps;
  };
}

const Contact = ({ data }: ContactInterface): JSX.Element | null => {
  if (!data) return null;

  const {
    prismicGeneralConfig: {
      data: { favicon },
    },
    allPrismicContact,
  } = data;

  const {
    page_title,
    meta_description,
    meta_image,
    form_id,
    portal_id,
    additional_info,
    form_title,
    form_subheading,
  } = allPrismicContact.edges[0].node.data;

  return (
    <Layout>
      <SEO
        title={page_title.text}
        description={meta_description?.text}
        image={meta_image}
        faviconHref={favicon?.url || ''}
      />
      <ContactSection backgroundColor="white">
        <Wrapper>
          <Container>
            <TextContainer>
              <Text fontSize="heading2" type="title" stringText={form_title.text} margin="none" />
              <Text type="subheading" richText={form_subheading.richText} margin="none" />
            </TextContainer>
            <ContactForm formId={form_id} portalId={portal_id} />
            {additional_info.richText && additional_info.richText.length > 0 && (
              <InfoText richText={additional_info.richText} fontSize="small" />
            )}
          </Container>
        </Wrapper>
      </ContactSection>
    </Layout>
  );
};

export default Contact;

export const query = graphql`
  query ContactPageQuery {
    prismicGeneralConfig {
      ...GeneralConfigFragment
    }
    allPrismicContact {
      edges {
        node {
          data {
            page_title {
              text
            }
            meta_description {
              text
            }
            meta_image {
              url
              alt
            }
            layout_type
            portal_id
            form_id
            form_title {
              text
            }
            form_subheading {
              richText
            }
            additional_info {
              richText
            }
          }
        }
      }
    }
  }
`;
